import { Trans } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { useNavigate } from "react-router";
import styles from "./Planning.module.scss";
import { useEffect } from "react";
import { usePatientOrActivityTitleFilterActions } from "./PlanningContext";
import { PlanningContextProvider } from "./PlanningContextComponents";
import { PlanningFilterBannerSection } from "./PlanningFilterBannerSection";
import { PlanningPatientOrActivityTitleFilterInput } from "./PlanningPatientOrActivityTitleFilterInput";
import {
  mapPositionSchema,
  usePlanningViewToolbar,
  viewModeSchema,
} from "./usePlanningViewToolbar";
import { PlanningSideBar } from "./SideBar/PlanningSideBar";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { ShiftsPanelContent } from "./ShiftsPanelContent";
import { MapProvider } from "react-map-gl/mapbox";
import clsx from "clsx";
import { PlanningMap } from "./PlanningMap";
import { useMap } from "react-map-gl/mapbox";
import useResizablePanels from "./useResizablePanels";
import { PageToolbar } from "@/components/PageToolbar/PageToolbar";
import { ControlledSelect } from "@medoma/components/Select/Select";
import { useLingui } from "@lingui/react/macro";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import ExpandFullIcon from "@medoma/components/icons/ExpandFullIcon";
import PinIcon from "@medoma/components/icons/PinIcon";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { DetailsPanel } from "./DetailsPanel/DetailsPanel";
import {
  selectedItemTypeSchema,
  useSelection,
} from "./Selection/SelectionContext";
import {
  selectableTimeSpans,
  NO_TIMESPAN,
  useManageTimeFilter,
  useTimeFilter,
} from "./useTimeFilter";
import { useCompetenceFilter } from "./useCompetenceFilter";
import { CompetenceFilterPicker } from "./CompetenceFilterPicker";
import { useTeamFilter } from "./useTeamFilter";
import { TeamFilterPicker } from "./TeamFilterPicker";
import { useUserProfile } from "@/api/Users";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import Form from "@/components/Form/Form";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  generatePlan,
  shiftsWithContentsQueryOptions,
  shiftKeys,
} from "@/api/Shifts";
import { DragAndDropContextProvider } from "./DragAndDrop/DragAndDropContextProvider";
import { SelectionContextProvider } from "./Selection/SelectionContextProvider";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { SingleSelectToggleButtonGroup } from "@medoma/components/ToggleButton/SingleSelectToggleButtonGroup";
import { SingleToggleButton } from "@medoma/components/ToggleButton/SingleToggleButton";
import * as Sentry from "@sentry/react";

export type IAllocatableItemType = "group" | "activityOccurrence" | undefined;

const TO_PLAN_PANEL_ID = "planning-to-plan-panel";
const SHIFTS_PANEL_ID = "planning-shifts-panel";
const DETAILS_PANEL_ID = "planning-details-panel";

export const Planning = () => {
  return (
    <PlanningContextProvider>
      <DragAndDropContextProvider>
        <SelectionContextProvider>
          <MapProvider>
            <InnerPlanning />
          </MapProvider>
        </SelectionContextProvider>
      </DragAndDropContextProvider>
    </PlanningContextProvider>
  );
};

const InnerPlanning = () => {
  const { t } = useLingui();
  const { organization } = useUserProfile();
  const { handleSubmit } = useForm();
  const queryClient = useQueryClient();
  const {
    isExpanded,
    setIsExpanded,
    isMapShown,
    setMapShown,
    mapPosition,
    viewMode,
    setViewMode,
  } = usePlanningViewToolbar();
  const { clearFilterValue: clearPatientOrActivityFilterValue } =
    usePatientOrActivityTitleFilterActions();

  const { data: showPlannedTimeInUI } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowPlannedTimeInUI,
  );

  const [timeFilter, setTimeFilter] = useManageTimeFilter();
  const {
    selected: selectedCompetences,
    allSelected: allCompetencesSelected,
    clear: clearCompetenceFilter,
  } = useCompetenceFilter();
  const {
    selected: selectedTeams,
    allSelected: allTeamsSelected,
    clear: clearTeamFilter,
  } = useTeamFilter();

  const navigate = useNavigate();

  const { planningMap } = useMap();

  const { selectedItem } = useSelection();

  // Handle the resizing of the to plan panel
  const {
    containerRef: toPlanShiftsAndMapContainerRef,
    handleRef: toPlanHandleRef,
    leadingPanelRef: toPlanPanelRef,
    trailingPanelRef: shiftsAndMapPanelRef,
    ariaValueNow: toPlanAriaValueNow,
    ariaValueMin: toPlanAriaValueMin,
    ariaValueMax: toPlanAriaValueMax,
  } = useResizablePanels({
    layout: "horizontal",
    initialLeadingPanelSize: 350,
    minPanelSize: 250,
    enabled: true,
    localStorageKey: "planning-to-plan-and-shifts-and-map-resizable-panel",
  });

  // Handle the resizing of the map panel
  const {
    containerRef: shiftsAndMapContainerRef,
    handleRef: shiftsAndMapHandleRef,
    leadingPanelRef: shiftsPanelRef,
    trailingPanelRef: mapPanelRef,
    ariaValueNow: shiftsAriaValueNow,
    ariaValueMin: shiftsAriaValueMin,
    ariaValueMax: shiftsAriaValueMax,
  } = useResizablePanels({
    layout:
      mapPosition === mapPositionSchema.enum.bottom ? "vertical" : "horizontal",
    initialLeadingPanelSize:
      mapPosition === mapPositionSchema.enum.bottom ? "50%" : "66%",
    minPanelSize: 100,
    enabled: isMapShown,
    localStorageKey: "planning-shifts-and-map-resizable-panel",
  });

  useEffect(() => {
    if (!planningMap) {
      // planningMap is probably closed, or just not loaded, so this is a no-op
      return;
    } else {
      planningMap.resize();
    }
  }, [planningMap]);

  const selectedDate = useSelectedDate();

  const parsedTimeFilter = useTimeFilter();
  const {
    data: shiftsWithContents,
    isPending,
    isError,
    error,
  } = useQuery(
    shiftsWithContentsQueryOptions(
      parsedTimeFilter
        ? {
            selectedDate,
            startTime: parsedTimeFilter.startTime,
            endTime: parsedTimeFilter.endTime,
          }
        : { selectedDate },
    ),
  );

  if (isError) {
    Sentry.captureException(
      new Error(`Failed to fetch shifts with contents: ${deducedError(error)}`),
    );
  }

  const { mutate: generatePlanMutation, isPending: isGeneratingPlan } =
    useMutation({
      mutationFn: () => generatePlan(selectedDate),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
      },
      onError: (error) => {
        console.error("Failed to generate plan:", error);
      },
    });

  // Resize the map when any panel is resized with debounce
  useEffect(() => {
    if (!shiftsPanelRef.current || !planningMap || !toPlanPanelRef.current) {
      return;
    }

    let resizeTimeoutId: NodeJS.Timeout | null = null;

    const resizeHandler = () => {
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }

      resizeTimeoutId = setTimeout(() => {
        planningMap.resize();
        resizeTimeoutId = null; // Clear timeout ID after execution
      }, 200);
    };

    const observer = new ResizeObserver(resizeHandler);
    observer.observe(shiftsPanelRef.current);
    observer.observe(toPlanPanelRef.current);

    return () => {
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      observer.disconnect();
    };
  }, [shiftsPanelRef, toPlanPanelRef, planningMap]);

  // Resize the map when the selected item changes to avoid empty space
  useEffect(() => {
    if (!planningMap) {
      return;
    }
    planningMap.resize();
  }, [planningMap, selectedItem]);

  const detailsPanelIsOpen = selectedItem
    ? selectedItem.type === selectedItemTypeSchema.enum.plannedVisit ||
      selectedItem.type ===
        selectedItemTypeSchema.enum.plannedActivityOccurrenceOrGroup ||
      selectedItem.type === selectedItemTypeSchema.enum.itemToPlan
    : false;

  // Handle the size ratio of map and details within the details-and-map panel (only right position)
  const enableResizingOfMapInDetailsPanel =
    isMapShown &&
    detailsPanelIsOpen &&
    mapPosition === mapPositionSchema.enum.right;

  const {
    handleRef: detailsAndMapHandleRef,
    leadingPanelRef: detailsPanelRef,
    trailingPanelRef: mapRef,
    ariaValueNow: detailsAriaValueNow,
    ariaValueMin: detailsAriaValueMin,
    ariaValueMax: detailsAriaValueMax,
  } = useResizablePanels({
    layout: "vertical",
    initialLeadingPanelSize: "66%",
    minPanelSize: 100,
    enabled: enableResizingOfMapInDetailsPanel,
    localStorageKey: "planning-details-and-map-resizable-panel",
    containerRef: mapPanelRef, // the container on the details and map is the same as the container for the map when the details panel is closed
  });

  // Resize the map when dragging the handle in the details panel
  useEffect(() => {
    if (!planningMap || !enableResizingOfMapInDetailsPanel || !mapRef.current) {
      return;
    }

    const observer = new ResizeObserver(() => {
      planningMap.resize();
    });
    observer.observe(mapRef.current);

    return () => {
      observer.disconnect();
    };
  }, [mapRef, planningMap, enableResizingOfMapInDetailsPanel]);

  const canFilterTime = selectableTimeSpans(organization).length >= 2;

  const clearAllFilters = () => {
    setTimeFilter(NO_TIMESPAN);
    clearPatientOrActivityFilterValue();
    clearCompetenceFilter();
    clearTeamFilter();
  };

  const selectedCompetenceCount = selectedCompetences.size;
  const competenceFilterBannerItem = allCompetencesSelected
    ? undefined
    : {
        name: t`Kompetenser`,
        value: plural(selectedCompetenceCount, {
          one: "# vald",
          other: "# valda",
        }),
        onClear: clearCompetenceFilter,
      };

  const selectedTeamCount = selectedTeams.size;
  const teamFilterBannerItem = allTeamsSelected
    ? undefined
    : {
        name: t`Team`,
        value: plural(selectedTeamCount, {
          one: "# valt",
          other: "# valda",
        }),
        onClear: clearTeamFilter,
      };

  return (
    <section className={styles.container}>
      <PageToolbar showDaySwitcher>
        <div className={styles.tools}>
          <search className={styles.filters}>
            <CompetenceFilterPicker />
            {canFilterTime ? (
              <ControlledSelect
                value={timeFilter}
                onChange={(newTimespan) => {
                  setTimeFilter(newTimespan);
                }}
                label={t`Tidsfilter`}
                hideLabel
                size="compact"
              >
                {selectableTimeSpans(organization).map(
                  ({ id, timespan, displayValue }) => {
                    return (
                      <option key={id} value={timespan}>
                        {t(displayValue)}
                      </option>
                    );
                  },
                )}
              </ControlledSelect>
            ) : null}
            <TeamFilterPicker />
            <PlanningPatientOrActivityTitleFilterInput />
          </search>
          <PlainButton onClick={() => navigate(`activities/new`)}>
            <PlusIcon />
            <Trans>Skapa aktivitet</Trans>
          </PlainButton>
          <div className={styles.viewOptions}>
            <SingleToggleButton
              aria-label={t`Expanderat läge`}
              isSelected={isExpanded}
              onChange={setIsExpanded}
            >
              <ExpandFullIcon />
            </SingleToggleButton>
            <SingleToggleButton
              aria-label={t`Visa karta`}
              isSelected={isMapShown}
              onChange={setMapShown}
            >
              <PinIcon />
            </SingleToggleButton>
            <SingleSelectToggleButtonGroup
              selectionMode="single"
              disallowEmptySelection
              selectedKey={viewMode}
              onSelectionChange={setViewMode}
              aria-label={t`Visningsläge`}
              items={[
                {
                  id: viewModeSchema.enum.timeline,
                  content: <Trans>Tidslinje</Trans>,
                },
                {
                  id: viewModeSchema.enum.list,
                  content: <Trans>Lista</Trans>,
                },
              ]}
            />
          </div>
          {showPlannedTimeInUI ? (
            <div className={styles.generatePlanButton}>
              <Form onSubmit={handleSubmit(() => generatePlanMutation())}>
                <FilledButton type="submit" disabled={isGeneratingPlan}>
                  <Trans>Skapa plan</Trans>
                </FilledButton>
              </Form>
            </div>
          ) : null}
        </div>
      </PageToolbar>

      <PlanningFilterBannerSection
        timeFilterBannerItem={
          timeFilter !== NO_TIMESPAN
            ? {
                name: t`Tid`,
                value: timeFilter,
                onClear: () => {
                  setTimeFilter(NO_TIMESPAN);
                },
              }
            : undefined
        }
        competenceFilterBannerItem={competenceFilterBannerItem}
        teamFilterBannerItem={teamFilterBannerItem}
        onClearAllFilters={clearAllFilters}
      />

      <div className={styles.outerWrapper}>
        <div
          ref={toPlanShiftsAndMapContainerRef}
          className={styles.innerWrapper}
        >
          <section
            id={TO_PLAN_PANEL_ID}
            className={styles.toPlanPanel}
            ref={toPlanPanelRef}
          >
            <PlanningSideBar />
          </section>
          {/* oxlint-disable jsx-a11y/prefer-tag-over-role -- This is a focusable window-splitter widget, not a static thematic break. */}
          <div
            ref={toPlanHandleRef}
            role="separator"
            aria-orientation="vertical"
            aria-label={t`Dra för att justera bredd`}
            aria-controls={TO_PLAN_PANEL_ID}
            aria-valuenow={toPlanAriaValueNow}
            aria-valuemin={toPlanAriaValueMin}
            aria-valuemax={toPlanAriaValueMax}
            className={styles.handle}
            title={t`Dra för att justera bredd`}
          />
          {/* oxlint-enable jsx-a11y/prefer-tag-over-role */}
          <div
            className={styles.shiftsAndMapPanelOuter}
            ref={shiftsAndMapPanelRef}
          >
            <div
              ref={shiftsAndMapContainerRef}
              className={clsx(
                styles.shiftsAndMapPanelInner,
                isMapShown ? "" : styles.mapHidden,
                isMapShown && mapPosition === "bottom"
                  ? styles.verticalLayout
                  : "",
              )}
            >
              {/* LIST OR TIMELINE VIEW */}
              <div
                id={SHIFTS_PANEL_ID}
                ref={shiftsPanelRef}
                className={styles.shiftsPanel}
              >
                {isPending ? (
                  <Loading message={t`Hämtar arbetspass`} padding={24} />
                ) : isError ? (
                  <ErrorMessage
                    message={`${t`Kunde inte hämta arbetspassen.`} ${deducedError(error)}`}
                    padding={24}
                  />
                ) : (
                  <ShiftsPanelContent
                    shiftsWithContents={shiftsWithContents}
                    viewMode={viewMode}
                  />
                )}
              </div>
              {/* oxlint-disable jsx-a11y/prefer-tag-over-role -- This is a focusable window-splitter widget, not a static thematic break. */}
              <div
                ref={shiftsAndMapHandleRef}
                role="separator"
                aria-orientation={
                  mapPosition === mapPositionSchema.enum.bottom
                    ? "horizontal"
                    : "vertical"
                }
                aria-label={
                  mapPosition === mapPositionSchema.enum.bottom
                    ? t`Dra för att justera höjd`
                    : t`Dra för att justera bredd`
                }
                aria-controls={SHIFTS_PANEL_ID}
                aria-valuenow={shiftsAriaValueNow}
                aria-valuemin={shiftsAriaValueMin}
                aria-valuemax={shiftsAriaValueMax}
                className={styles.handle}
                title={
                  mapPosition === mapPositionSchema.enum.bottom
                    ? t`Dra för att justera höjd`
                    : t`Dra för att justera bredd`
                }
              />
              {/* oxlint-enable jsx-a11y/prefer-tag-over-role */}
              {isMapShown ? (
                <div
                  ref={mapPanelRef}
                  className={clsx(
                    styles.detailsAndMap,
                    mapPosition === "bottom"
                      ? styles.horizontalLayout
                      : styles.verticalLayout,
                  )}
                >
                  {detailsPanelIsOpen ? (
                    <div
                      id={DETAILS_PANEL_ID}
                      ref={detailsPanelRef}
                      className={styles.detailsPanel}
                    >
                      <DetailsPanel />
                    </div>
                  ) : null}
                  {/* oxlint-disable jsx-a11y/prefer-tag-over-role -- This is a focusable window-splitter widget, not a static thematic break. */}
                  {enableResizingOfMapInDetailsPanel ? (
                    <div
                      ref={detailsAndMapHandleRef}
                      role="separator"
                      aria-orientation="horizontal"
                      aria-label={t`Dra för att justera höjd`}
                      aria-controls={DETAILS_PANEL_ID}
                      aria-valuenow={detailsAriaValueNow}
                      aria-valuemin={detailsAriaValueMin}
                      aria-valuemax={detailsAriaValueMax}
                      className={styles.handle}
                      title={t`Dra för att justera höjd`}
                    />
                  ) : null}
                  {/* oxlint-enable jsx-a11y/prefer-tag-over-role */}
                  <div ref={mapRef} className={styles.map}>
                    <PlanningMap />
                  </div>
                </div>
              ) : detailsPanelIsOpen ? (
                <div
                  ref={mapPanelRef}
                  className={clsx(
                    styles.detailsPanel,
                    styles.detailsPanelNoMap,
                  )}
                >
                  <DetailsPanel />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
